import { useCallback, useEffect, useState } from 'react'

import { admin } from '../../lib/api/admin.ts'
import type { AgentCommand } from '../../lib/api/admin.ts'
import { AthenaError } from '../../lib/api/client.ts'
import { describeError } from '../../lib/api/messages.ts'
import { machines } from '../../lib/api/resources.ts'
import type { WebException } from '../../lib/api/types.ts'
import { isAwaitingAgent, isConfirmed, openCommandIdOf } from '../../lib/commands/semantics.ts'
import { useCommandTracking } from '../../lib/commands/useCommandTracking.ts'
import { formatDayClock, formatInt, NO_VALUE } from '../../lib/format.ts'
import { useAction } from '../../lib/useAction.ts'
import { useResource } from '../../lib/useResource.ts'
import { availabilityOf, revokeExceptionOutcome, revokeWebExceptionParams } from '../../lib/webdefense/semantics.ts'
import { ReleaseStatus } from '../commands/CommandState.tsx'
import { Button } from '../primitives/Button.tsx'
import { ConfirmDialog, Dialog } from '../primitives/Dialog.tsx'
import { EmptyState, ErrorState, LoadingRow, Notice } from '../primitives/States.tsx'
import { TABLE, TD, TH, THEAD, TR } from '../primitives/table.ts'
import { WebCommandTracking } from '../webdefense/WebCommandTracking.tsx'

type Listing = { id: string; initial: AgentCommand | null }

type Revoke =
  | { kind: 'confirm'; item: WebException; key: string }
  | { kind: 'track'; item: WebException; id: string; initial: AgentCommand | null }
  | null

/**
 * Exceções do Web Defense de UMA máquina: regras que um operador tirou de uma
 * rota com "Falso positivo da aplicação". Valem para todos os IPs.
 *
 * Só o agente sabe a lista. O Athena guarda a última listagem confirmada e já
 * desconta dela as revogações confirmadas depois: revogar não exige listar de
 * novo, só reler.
 */
export function MachineWebExceptionsDialog({
  machine,
  onClose,
}: {
  machine: { id: string; label: string }
  onClose: () => void
}) {
  const request = useAction()
  const revokeAction = useAction()
  const [listing, setListing] = useState<Listing | null>(null)
  const [revoke, setRevoke] = useState<Revoke>(null)

  const catalogFetcher = useCallback(
    (signal: AbortSignal) =>
      admin.commandCatalog(machine.id, signal).catch((cause: unknown) => {
        if (signal.aborted) throw cause
        return null
      }),
    [machine.id],
  )
  const catalog = useResource(catalogFetcher)
  const canList = availabilityOf(catalog.data, 'list_web_exceptions')
  const canRevoke = availabilityOf(catalog.data, 'revoke_web_exception')

  const listFetcher = useCallback((signal: AbortSignal) => machines.webExceptions(machine.id, signal), [machine.id])
  const list = useResource(listFetcher)
  const neverListed = list.error instanceof AthenaError && list.error.code === 'web_exceptions_not_listed'
  const reloadList = list.reload

  const ask = (): void =>
    request.run(async () => {
      try {
        const command = await admin.enqueueCommand(machine.id, {
          kind: 'list_web_exceptions',
          params: null,
          reason: null,
          idempotencyKey: crypto.randomUUID(),
          acknowledgeNotDeliverable: false,
        })
        setListing({ id: command.id, initial: command })
      } catch (cause) {
        // Já há uma listagem aberta nesta máquina: acompanhe aquela.
        if (cause instanceof AthenaError && cause.code === 'agent_command_already_open') {
          const id = openCommandIdOf(cause.problem.detail)
          if (id !== null) {
            setListing({ id, initial: null })
            return
          }
        }
        throw cause
      }
    })

  const sendRevoke = (item: WebException, idempotencyKey: string): void =>
    revokeAction.run(async () => {
      const command = await admin.enqueueCommand(machine.id, {
        kind: 'revoke_web_exception',
        params: revokeWebExceptionParams(item.id),
        reason: null,
        idempotencyKey,
        acknowledgeNotDeliverable: false,
      })
      setRevoke({ kind: 'track', item, id: command.id, initial: command })
    })

  const onListed = useCallback(() => {
    setListing(null)
    reloadList()
  }, [reloadList])

  const context = (item: WebException) => ({ rule: item.rule ?? item.id, route: item.route ?? '/' })
  const data = list.data

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Exceções web"
      description={<span className="font-medium text-fg">{machine.label}</span>}
      wide="xl"
    >
      <div className="space-y-4">
        {!canList.available && <Notice tone="warn">{canList.reason}</Notice>}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={data === null ? 'primary' : 'default'}
            busy={request.busy}
            // Com uma listagem aberta, o 409 leva até ela: o botão não precisa travar.
            disabled={!canList.available}
            onClick={ask}
          >
            {data === null ? 'Listar exceções' : 'Atualizar'}
          </Button>
          {data !== null && (
            <span className="tnum text-micro text-fg-muted">listado às {formatDayClock(data.as_of)} UTC</span>
          )}
          {request.error !== null && (
            <p role="alert" className="text-sm text-crit">
              {describeError(request.error)}
            </p>
          )}
        </div>

        {listing !== null && (
          <ListingProgress key={listing.id} commandId={listing.id} initial={listing.initial} onListed={onListed} />
        )}

        {list.error !== null && !neverListed ? (
          <ErrorState error={list.error} onRetry={list.reload} />
        ) : neverListed ? (
          <EmptyState title="As exceções desta máquina nunca foram listadas." />
        ) : data === null ? (
          <LoadingRow />
        ) : (
          <section className="space-y-2">
            <header className="flex flex-wrap items-baseline gap-x-2">
              <h3 className="text-sm font-medium text-fg">Exceções</h3>
              <span className="tnum text-micro text-fg-muted">{data.exceptions.length}</span>
              {data.revoked_since.length > 0 && (
                <span className="text-micro text-fg-muted">
                  · {formatInt(data.revoked_since.length)} revogada(s) depois desta listagem
                </span>
              )}
            </header>

            {data.omitted > 0 && (
              <Notice tone="warn">Lista incompleta: mais {formatInt(data.omitted)} exceção(ões).</Notice>
            )}

            {data.exceptions.length === 0 ? (
              <EmptyState title="Nenhuma exceção nesta máquina." />
            ) : (
              <table className={TABLE}>
                <thead className={THEAD}>
                  <tr>
                    <th scope="col" className={`${TH} w-[34%]`}>Regra</th>
                    <th scope="col" className={`${TH} w-[16%]`}>Rota</th>
                    <th scope="col" className={`${TH} w-[10%]`}>Por</th>
                    <th scope="col" className={TH}>Desde</th>
                    <th scope="col" className={TH}>Nota</th>
                    <th scope="col" className={`${TH} text-right`}>
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.exceptions.map((item) => (
                    <tr key={item.id} className={TR}>
                      <th scope="row" className={`${TD} max-w-0 text-left font-normal`}>
                        <span className="block truncate font-mono text-fg" title={item.rule ?? item.id}>
                          {item.rule ?? '—'}
                        </span>
                      </th>
                      <td className={`${TD} max-w-0`}>
                        <span className="block truncate font-mono text-fg-secondary" title={item.route ?? undefined}>
                          {item.route ?? '—'}
                        </span>
                      </td>
                      <td className={`${TD} max-w-0`}>
                        <span className="block truncate font-mono text-micro text-fg-secondary" title={item.by ?? undefined}>
                          {item.by ?? '—'}
                        </span>
                      </td>
                      <td className={`${TD} tnum whitespace-nowrap`}>{since(item.since)}</td>
                      <td className={`${TD} max-w-0`}>
                        <span className="block truncate text-fg-secondary" title={item.note ?? undefined}>
                          {item.note ?? '—'}
                        </span>
                      </td>
                      <td className={`${TD} text-right`}>
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={!canRevoke.available}
                          title={canRevoke.reason ?? undefined}
                          onClick={() => {
                            revokeAction.reset()
                            setRevoke({ kind: 'confirm', item, key: crypto.randomUUID() })
                          }}
                        >
                          Revogar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        )}
      </div>

      {revoke?.kind === 'confirm' && (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setRevoke(null)}
          title="Revogar exceção?"
          consequence={
            <span className="block">
              A regra <span className="font-mono text-fg">{context(revoke.item).rule}</span> volta a valer em{' '}
              <span className="font-mono text-fg">{context(revoke.item).route}</span> para todos os IPs.
            </span>
          }
          confirmLabel="Revogar"
          busy={revokeAction.busy}
          error={revokeAction.error !== null ? describeError(revokeAction.error) : null}
          onConfirm={() => sendRevoke(revoke.item, revoke.key)}
        />
      )}

      {revoke?.kind === 'track' && (
        <Dialog
          open
          onOpenChange={(open) => !open && setRevoke(null)}
          title="Revogar exceção"
          description={<span className="font-mono text-fg">{context(revoke.item).rule}</span>}
          wide
        >
          <WebCommandTracking
            key={revoke.id}
            commandId={revoke.id}
            initial={revoke.initial}
            outcomeOf={(state) => revokeExceptionOutcome(state, context(revoke.item))}
            toastLabel={machine.label}
            // O Athena já tira a exceção da lista: basta reler.
            onSettled={reloadList}
            onClose={() => setRevoke(null)}
          />
        </Dialog>
      )}
    </Dialog>
  )
}

/** Um pedido de listagem desta sessão, até o agente responder. */
function ListingProgress({
  commandId,
  initial,
  onListed,
}: {
  commandId: string
  initial: AgentCommand | null
  onListed: () => void
}) {
  const { command, error, refresh } = useCommandTracking(commandId, initial)
  const state = command?.state ?? null
  const listed = state !== null && isConfirmed(state)

  useEffect(() => {
    if (listed) onListed()
  }, [listed, onListed])

  if (command === null) {
    return error !== null ? <ErrorState error={error} onRetry={refresh} /> : <LoadingRow label="Lendo o pedido…" />
  }
  if (listed) return null

  const awaiting = isAwaitingAgent(command.state)
  return (
    <ReleaseStatus
      command={command}
      outcome={{
        tone: awaiting ? 'accent' : command.state === 'failed' ? 'crit' : 'warn',
        headline: awaiting
          ? 'Aguardando o agente responder com a lista.'
          : command.state === 'failed'
            ? 'O agente não listou as exceções.'
            : 'O pedido não chegou ao agente. Liste de novo.',
      }}
    />
  )
}

/** O agente manda a data como texto; se não for uma data legível, ela vai como veio. */
function since(value: string | null): string {
  if (value === null) return NO_VALUE
  const formatted = formatDayClock(value)
  return formatted === NO_VALUE ? value : formatted
}
